import type { Options } from '@/models/selectBox';
import { calcPriceIncludingTax } from '@/utils/priceIncludingTax';
import { DEFAULT_TAX_RATE } from '@/wabunka-src/constants/enExperiences';

import type { WabunkaExperienceExperienceOption, WabunkaExperienceOption, WabunkaExperienceOptionDetailedImage } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_option';

/** 体験オプションの詳細画像 */
export interface WabunkaExperienceOptionDetailedImageViewModel {
  /** ID */
  id: string;
  /** オプションID */
  experienceOptionId: string;
  /** 画像のファイルパス */
  imageFilepath: string;
  /** シーケンスID */
  sequenceId: number;
}
/**
 * Wabunka 体験オプションビューモデル
 */
export interface WabunkaExperienceOptionViewModel {
  /** 体験オプションマスタID */
  id: string;
  /** 体験オプション名 */
  name: string;
  /** 管理用名称 */
  nameForAdmin: string;
  /** 説明 */
  description: string;
  /** 詳細説明 */
  detailedDescription: string;
  /** 注釈 */
  annotation: string;
  /** サムネイル画像のファイルパス */
  mainImageFilepath: string;
  /** 金額（税抜） */
  price: number;
  /** シーケンスID */
  sequenceId: number;
  /** 体験オプション詳細 */
  detailedImages: WabunkaExperienceOptionDetailedImageViewModel[];
  /**
   * 追加プロパティ
   */
  priceIncludingTax: number;
}

/**
 * 体験オプションビューモデル のデフォルト値を返却する
 *
 * @returns {WabunkaExperienceOptionViewModel} - 体験オプションビューモデルのデフォルト値
 */
export const createDefaultWabunkaExperienceOptionViewModel = (): WabunkaExperienceOptionViewModel => ({
  id: '',
  name: '',
  nameForAdmin: '',
  description: '',
  detailedDescription: '',
  annotation: '',
  mainImageFilepath: '',
  price: 0,
  sequenceId: 1,
  detailedImages: [],
  priceIncludingTax: 0,
});

/**
 * セレクトボックス用配列の組み立て
 * @param {WabunkaExperienceEOptionViewModel} experienceOptions - 体験オプションビューモデル
 * @returns {Options} - 選択肢の配列
 */
export const buildWabunkaExperienceOptionChoices = (experienceOptions: WabunkaExperienceOptionViewModel[]): Options => {
  if (!experienceOptions) {
    return [];
  }

  return experienceOptions
    .sort((a, b) => {
      // シーケンスIDの昇順でソート
      return a.sequenceId - b.sequenceId;
    })
    .map((_) => ({ label: `${_.name}（Paid : +JPY ${_.priceIncludingTax}）`, value: _.id }));
};

export const buildWabunkaExperienceOptionDetailedImageListViewModel = (res: WabunkaExperienceOptionDetailedImage[]): WabunkaExperienceOptionDetailedImageViewModel[] => {
  return res
    .map((experienceOptionDetailedImage) => ({
      id: experienceOptionDetailedImage.id,
      experienceOptionId: experienceOptionDetailedImage.experienceOptionId,
      imageFilepath: experienceOptionDetailedImage.imageFilepath,
      sequenceId: experienceOptionDetailedImage.sequenceId,
    }))
    .sort((a, b) => a.sequenceId - b.sequenceId);
};

export const buildWabunkaExperienceOptionListViewModel = (res: WabunkaExperienceExperienceOption[]): WabunkaExperienceOptionViewModel[] => {
  return res
    .filter((experienceOption) => experienceOption !== undefined)
    .map((experienceOption) => {
      const option = experienceOption.experienceOption as WabunkaExperienceOption;
      return {
        id: option.id,
        name: option.name,
        nameForAdmin: option.nameForAdmin,
        description: option.description,
        detailedDescription: option.detailedDescription,
        annotation: option.annotation,
        mainImageFilepath: option.mainImageFilepath,
        price: option.price,
        sequenceId: experienceOption.sequenceId,
        detailedImages: buildWabunkaExperienceOptionDetailedImageListViewModel(option.detailedImages),
        priceIncludingTax: calcPriceIncludingTax(option.price, DEFAULT_TAX_RATE),
      };
    })
    .sort((a, b) => a.sequenceId - b.sequenceId);
};
